import React from 'react';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { IconCamera, IconPhone, IconPrinter, IconHash } from '@tabler/icons-react';
import FormObserver from '../../../components/UnsavedChanges/FormObserver';
import UnsavedChangesModal from '../../../components/UnsavedChanges/UnsavedChangesModal';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import './OrganizationDetails.css';

const formatUSPhone = (value) => {
  const phoneNumber = parsePhoneNumberFromString(value, 'US');
  return phoneNumber ? phoneNumber.formatNational() : value;
};

// Get organization initials from name
const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

// Organization Details Schema
const OrganizationSchema = Yup.object().shape({
  org_name: Yup.string().trim().required('Organization name is required'),
  org_phone_number: Yup.string()
    .matches(
      /^\d{10}$/,
      'Enter a valid 10-digit phone number (e.g., 5551234567).',
    )
    .nullable(),
  org_fax_number: Yup.string()
    .matches(
      /^\d{10}$/,
      'Enter a valid 10-digit fax number (e.g., 5551234567).',
    )
    .nullable()
    .optional(),
  org_npi_number: Yup.string()
    .matches(
      /^\d{10}$/,
      'NPI number must be exactly 10 digits and contain only numbers',
    )
    .nullable()
    .optional(),
  org_street: Yup.string().trim().nullable(),
  org_city: Yup.string().trim().nullable(),
  org_state: Yup.string().trim().nullable(),
  org_zipcode: Yup.string()
    .trim()
    .matches(
      /^\d{5}(-\d{4})?$/,
      'Enter a valid ZIP Code (e.g., 12345 or 12345-6789)',
    )
    .nullable(),
});

const OrganizationDetails = ({
  initialValues,
  isLoading,
  handleSubmit,
  handleFormDirtyChange,
  previewImage,
  handleImageClick,
  handleImageChange,
  fileInputRef,
  formikRef,
  showUnsavedModal,
  handleStayOnPage,
  handleDiscardChanges,
}) => {
  if (isLoading) {
    return (
      <div className="text-center p-5">Loading organization details...</div>
    );
  }

  const orgInitials = getInitials(initialValues.org_name);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={OrganizationSchema}
      onSubmit={handleSubmit}
      enableReinitialize={true}
      innerRef={formikRef}
    >
      {({ errors, touched, isSubmitting, dirty, values }) => (
        <Form>
          <FormObserver onChange={handleFormDirtyChange} />
          
          {/* Practice Details Card */}
          <div className="practice-details-card">
            <div className="practice-card-header">
              <h2>Organization Details</h2>
              <p>Update your organization's details and branding</p>
            </div>

            {/* Logo Section */}
            <div className="practice-logo-section">
              <div className="practice-avatar-container">
                <div className="practice-avatar-large">
                  {previewImage ? (
                    <img src={previewImage} alt="Practice logo" />
                  ) : (
                    <span className="practice-avatar-initials">{orgInitials}</span>
                  )}
                </div>
              </div>
              
              <div className="practice-info">
                <div className="practice-name">{values.org_name || 'Practice Name'}</div>
              </div>

              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
              
              <button
                type="button"
                className="btn-upload-logo"
                onClick={handleImageClick}
              >
                <IconCamera size={18} />
                Upload Logo
              </button>
            </div>

            {/* Divider */}
            <hr className="logo-section-divider" />

            {/* Form Fields - 2 Column Grid */}
            <div className="form-grid-2col">
              {/* Practice Name */}
              <div className="form-field">
                <label htmlFor="org_name">Practice Name</label>
                <Field
                  type="text"
                  name="org_name"
                  id="org_name"
                  className={`org-form-control ${
                    errors.org_name && touched.org_name ? 'is-invalid' : ''
                  }`}
                  placeholder="Metropolitan Medical Center"
                  disabled={isSubmitting}
                />
                {errors.org_name && touched.org_name && (
                  <span className="field-error">{errors.org_name}</span>
                )}
              </div>

              {/* Phone Number */}
              <div className="form-field">
                <label htmlFor="org_phone_number">Phone Number</label>
                <div className="input-with-icon">
                  <IconPhone className="input-icon-left" size={18} />
                  <Field name="org_phone_number">
                    {({ field, form }) => {
                      const rawDigits = field.value.replace(/\D/g, '').slice(0, 10);
                      const displayValue = formatUSPhone(rawDigits);

                      return (
                        <input
                          {...field}
                          type="text"
                          className={`org-form-control ${
                            form.errors.org_phone_number &&
                            form.touched.org_phone_number
                              ? 'is-invalid'
                              : ''
                          }`}
                          id="org_phone_number"
                          placeholder="(555) 123-4567"
                          disabled={isSubmitting}
                          value={displayValue}
                          onChange={(e) => {
                            const digitsOnly = e.target.value
                              .replace(/\D/g, '')
                              .slice(0, 10);
                            form.setFieldValue(field.name, digitsOnly);
                          }}
                          onBlur={() => form.setFieldTouched(field.name, true)}
                        />
                      );
                    }}
                  </Field>
                </div>
                {errors.org_phone_number && touched.org_phone_number && (
                  <span className="field-error">{errors.org_phone_number}</span>
                )}
              </div>

              {/* Fax Number */}
              <div className="form-field">
                <label htmlFor="org_fax_number">Fax Number</label>
                <div className="input-with-icon">
                  <IconPrinter className="input-icon-left" size={18} />
                  <Field name="org_fax_number">
                    {({ field, form }) => {
                      const rawDigits = field.value.replace(/\D/g, '').slice(0, 10);
                      const displayValue = formatUSPhone(rawDigits);

                      return (
                        <input
                          {...field}
                          type="text"
                          className={`org-form-control ${
                            form.errors.org_fax_number &&
                            form.touched.org_fax_number
                              ? 'is-invalid'
                              : ''
                          }`}
                          id="org_fax_number"
                          placeholder="(555) 987-6544"
                          disabled={isSubmitting}
                          value={displayValue}
                          onChange={(e) => {
                            const digitsOnly = e.target.value
                              .replace(/\D/g, '')
                              .slice(0, 10);
                            form.setFieldValue(field.name, digitsOnly);
                          }}
                          onBlur={() => form.setFieldTouched(field.name, true)}
                        />
                      );
                    }}
                  </Field>
                </div>
                {errors.org_fax_number && touched.org_fax_number && (
                  <span className="field-error">{errors.org_fax_number}</span>
                )}
              </div>
            </div>

            {/* NPI Number - Full Width */}
            <div className="form-field form-field-full">
              <label htmlFor="org_npi_number">NPI Number</label>
              <div className="input-with-icon">
                <IconHash className="input-icon-left" size={18} />
                <Field
                  type="text"
                  name="org_npi_number"
                  id="org_npi_number"
                  className={`org-form-control ${
                    errors.org_npi_number && touched.org_npi_number
                      ? 'is-invalid'
                      : ''
                  }`}
                  placeholder="1234567890"
                  disabled={isSubmitting}
                />
              </div>
              {errors.org_npi_number && touched.org_npi_number && (
                <span className="field-error">{errors.org_npi_number}</span>
              )}
            </div>

            {/* Practice Address - Single Row */}
            <div className="form-grid-address">
              {/* Street */}
              <div className="form-field address-street">
                <label htmlFor="org_street">Practice Address</label>
                <Field
                  type="text"
                  name="org_street"
                  id="org_street"
                  className={`org-form-control ${
                    errors.org_street && touched.org_street ? 'is-invalid' : ''
                  }`}
                  placeholder="123 Medical Plaza"
                  disabled={isSubmitting}
                />
                {errors.org_street && touched.org_street && (
                  <span className="field-error">{errors.org_street}</span>
                )}
              </div>

              {/* City */}
              <div className="form-field address-city">
                <label htmlFor="org_city">&nbsp;</label>
                <Field
                  type="text"
                  name="org_city"
                  id="org_city"
                  className={`org-form-control ${
                    errors.org_city && touched.org_city ? 'is-invalid' : ''
                  }`}
                  placeholder="New York"
                  disabled={isSubmitting}
                />
                {errors.org_city && touched.org_city && (
                  <span className="field-error">{errors.org_city}</span>
                )}
              </div>

              {/* State */}
              <div className="form-field address-state">
                <label htmlFor="org_state">&nbsp;</label>
                <Field
                  type="text"
                  name="org_state"
                  id="org_state"
                  className={`org-form-control ${
                    errors.org_state && touched.org_state ? 'is-invalid' : ''
                  }`}
                  placeholder="NY"
                  disabled={isSubmitting}
                />
                {errors.org_state && touched.org_state && (
                  <span className="field-error">{errors.org_state}</span>
                )}
              </div>

              {/* ZIP Code */}
              <div className="form-field address-zip">
                <label htmlFor="org_zipcode">&nbsp;</label>
                <Field
                  type="text"
                  name="org_zipcode"
                  id="org_zipcode"
                  className={`org-form-control ${
                    errors.org_zipcode && touched.org_zipcode
                      ? 'is-invalid'
                      : ''
                  }`}
                  placeholder="10001"
                  disabled={isSubmitting}
                />
                {errors.org_zipcode && touched.org_zipcode && (
                  <span className="field-error">{errors.org_zipcode}</span>
                )}
              </div>
            </div>

            {/* Form Footer */}
            <div className="org-form-footer">
              <button
                type="submit"
                className="btn-save-org"
                disabled={isSubmitting || !dirty}
              >
                {isSubmitting ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </button>
            </div>
          </div>

          {/* Unsaved Changes Modal */}
          <UnsavedChangesModal
            show={showUnsavedModal}
            onStay={handleStayOnPage}
            onDiscard={handleDiscardChanges}
          />
        </Form>
      )}
    </Formik>
  );
};

export default OrganizationDetails;
